/**
 * MENU CLIC DROIT DE L'ACCÈS — les calculs purs (lot 2, 24/10/2026).
 *
 * Rien ici ne touche React, le routeur ni le document : ces décisions se
 * testent sans navigateur (tests/menu-acces.test.ts). `menu-acces.tsx` les
 * applique. Aucun import, à dessein : les adresses des pages sont recopiées
 * (un test vérifie qu'elles restent égales à celles des pages).
 *
 * Deux identifiants à ne pas confondre : l'id UniFi Access (fiche Access,
 * historique `?personne=`) et le `personId` du salarié lié (dossier RH,
 * validation des heures, planning `?personne=`). Le second n'est connu que
 * sur certaines listes.
 */

const enc = encodeURIComponent

/* ─── les adresses : celles que les pages du module écrivent déjà ─── */

/** Fiche porte (AccessModule.tsx, liste « Portes & espaces »). */
export const cheminPorte = (id: string): string => `/securite/controle-acces/portes/${enc(id)}`

/** Fiche personne Access (PeoplePage.tsx). L'id est celui d'UniFi Access. */
export const cheminPersonneAccess = (id: string): string => `/securite/controle-acces/personnes/${enc(id)}`

/**
 * Les passages d'une porte sur 24 h : « Tout l’historique de cette porte »
 * (fiche porte) avec la période que l'historique lit déjà (`periode=24h`).
 */
export const cheminPassagesPorte = (id: string): string => `/securite/controle-acces/historique?porte=${enc(id)}&periode=24h`

/**
 * Les passages d'une porte sur la nuit en cours, de 08:00 à 08:00 (la nuit
 * que le serveur fixe, history-period.ts). Recopie de `historique-modele.ts`
 * (le lien « Passages de la nuit en cours » de la fiche porte) : la clé
 * d'adresse reste `soiree`, le libellé dit « nuit » (décision d'Hugo).
 */
export const cheminPassagesPorteSoiree = (id: string): string =>
  `/securite/controle-acces/historique?porte=${encodeURIComponent(id)}&periode=soiree`

/** Les passages d'une personne : « Voir tout l’historique » de sa fiche Access. */
export const cheminPassagesPersonne = (id: string): string => `/securite/controle-acces/historique?personne=${enc(id)}&identifie=1`

/** Dossier RH du salarié lié — la route qui existe (App.tsx), pas /equipe/rh/personnes. */
export const cheminDossierRh = (personId: string): string => `/equipe/rh/salaries/${enc(personId)}`

/** Validation des heures, ouverte sur le salarié (`?person=`, ValidationWorkstation). */
export const cheminValidationHeures = (personId: string): string => `/equipe/temps/validation?person=${enc(personId)}`

/**
 * Le planning d'UNE personne : filtre exact sur le person.id RH (PlanningPage,
 * `?personne=`), lu au montage seulement. Recopie de `rh/planning-personne.ts`.
 * Attention : ce `?personne=` porte le person.id RH, celui de l'historique
 * Access (`cheminPassagesPersonne`) porte l'id UniFi.
 */
export const cheminPlanningPersonne = (personId: string): string =>
  `/equipe/temps/planning?${new URLSearchParams({ personne: personId }).toString()}`

/* ─── coordonnées ─── */

/**
 * L'e-mail tel qu'on peut le copier : seulement s'il est affiché EN CLAIR.
 * Sans access.identity.sensitive.view, le serveur le remplace par « ••• »
 * (packages/core/src/access/queries.ts, mask) : rien à copier.
 */
export function emailEnClair(email: string | null | undefined): string | null {
  const e = (email ?? '').trim()
  if (!e || /^[•·.*\s]+$/.test(e)) return null
  return e
}

/* ─── Déverrouiller : présent, grisé ou absent ─── */

export type EtatEntree = { visible: false } | { visible: true; indisponible: string | null }

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)

/**
 * L'entrée « Déverrouiller » d'un menu porte.
 *
 *   · ABSENTE sans access.doors.unlock ET access.doors.view : la commande
 *     demande le premier, la relecture fraîche de la porte le second. Absente
 *     aussi tant que les droits ne sont pas lus (« pas encore su » ≠ « oui »).
 *     La fiche porte, elle, grise son bouton : le menu est plus strict.
 *   · GRISÉE avec la raison du serveur quand /access/capabilities ne la dit
 *     pas AVAILABLE (mode sûr, périmètre, action différée) — la même source
 *     que le bouton de la fiche (ActionButton, lib.tsx).
 *   · GRISÉE « capacités illisibles » quand cette lecture a été refusée
 *     (403 : la réponse n'a pas de liste d'actions). Rien n'est tenté à
 *     l'aveugle ; le serveur resterait de toute façon l'arbitre.
 */
export function etatDeverrouillage(droits: { permissions: readonly string[]; loading: boolean }, caps: unknown): EtatEntree {
  if (droits.loading) return { visible: false }
  if (!droits.permissions.includes('access.doors.unlock') || !droits.permissions.includes('access.doors.view')) return { visible: false }
  if (caps === null || caps === undefined) return { visible: true, indisponible: 'Capacités pas encore lues : rouvrez le menu' }
  const actions = objet(caps) ? caps['actions'] : undefined
  if (!Array.isArray(actions)) return { visible: true, indisponible: 'Capacités illisibles : Gestion ne peut pas dire si le déverrouillage est permis' }
  const info = actions.find((a): a is Record<string, unknown> => objet(a) && a['action'] === 'ACCESS_DOOR_UNLOCK')
  if (info && info['status'] !== 'AVAILABLE') {
    const raison = info['reason']
    return { visible: true, indisponible: typeof raison === 'string' && raison.trim() ? raison : 'Déverrouillage indisponible pour le moment' }
  }
  return { visible: true, indisponible: null }
}

/* ─── la caméra de la porte : présente, grisée ou absente ─── */

/** Motif d'uid du registre de Sonde, recopié de `camera-porte-modele.ts` (UID_CAMERA). */
const UID_CAMERA = /^[a-z0-9][a-z0-9-]{1,62}$/

export type EntreeCamera = { visible: false } | { visible: true; uid: string } | { visible: true; indisponible: string }

/**
 * L'entrée « Voir la caméra de la porte » (la fiche Sonde de la caméra que la
 * fiche porte relie) :
 *
 *   · ABSENTE sans le droit (access.doors.view ET sonde.read, droits lus) ;
 *   · ABSENTE quand l'écran ne connaît pas la caméra (`undefined` : passages,
 *     règles, appareils) — plutôt que d'affirmer « aucune » à tort ;
 *   · GRISÉE « Aucune caméra reliée à cette porte » pour `null` ;
 *   · ABSENTE pour un uid hors motif : jamais une adresse douteuse ;
 *   · sinon visible, avec l'uid.
 */
export function entreeCameraPorte(peut: boolean, camera: string | null | undefined): EntreeCamera {
  if (!peut || camera === undefined) return { visible: false }
  if (camera === null) return { visible: true, indisponible: 'Aucune caméra reliée à cette porte' }
  return UID_CAMERA.test(camera) ? { visible: true, uid: camera } : { visible: false }
}
